/**
 * Clickable currency amount component
 * Displays amount with currency toggle functionality
 */

import React from 'react';
import { useCurrencyManager } from '../hooks/useCurrencyManager';

interface CurrencyAmountProps {
  amount: number;
  sourceCurrency?: 'USD' | 'JPY';
  className?: string;
  showToggleHint?: boolean;
}

export const CurrencyAmount: React.FC<CurrencyAmountProps> = React.memo(({
  amount,
  sourceCurrency = 'USD',
  className = '',
  showToggleHint = false
}) => {
  const {
    currency,
    exchangeRate,
    isLoadingRate,
    isFallbackRate,
    rateTimestamp,
    toggleCurrency,
    formatAmountSync
  } = useCurrencyManager();

  const formattedAmount = formatAmountSync(amount, sourceCurrency);
  const isConverted = sourceCurrency !== currency;

  const tooltipParts: string[] = [`Click to switch to ${currency === 'USD' ? 'JPY' : 'USD'}`];
  if (exchangeRate) {
    tooltipParts.push(`Rate: ¥${exchangeRate.toFixed(2)}`);
  }
  if (rateTimestamp) {
    tooltipParts.push(`Updated: ${new Date(rateTimestamp).toLocaleString()}`);
  }
  if (isFallbackRate) {
    tooltipParts.push('Fallback rate in use');
  }

  const tooltip = tooltipParts.join(' | ');

  return (
    <span
      onClick={toggleCurrency}
      className={`inline-flex items-center gap-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 py-0.5 transition-colors ${className}`}
      title={tooltip}
    >
      <span className={`font-medium ${isConverted ? 'text-blue-600 dark:text-blue-400' : ''}`}>
        {formattedAmount}
      </span>
      
      {/* Exchange rate indicator */}
      {isConverted && exchangeRate && (
        <svg 
          className="w-3 h-3 text-blue-500 dark:text-blue-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" 
          />
        </svg>
      )}
      
      {isConverted && isFallbackRate && (
        <span className="text-[10px] uppercase tracking-wide text-orange-500 dark:text-orange-400 font-semibold" title="Fallback exchange rate in use">
          Fallback
        </span>
      )}

      {/* Loading indicator */}
      {isLoadingRate && (
        <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-400"></div>
      )}
      
      {/* Toggle hint */}
      {showToggleHint && (
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
          (click to toggle)
        </span>
      )}
    </span>
  );
});
CurrencyAmount.displayName = 'CurrencyAmount';
